import { Injectable, NgZone } from '@angular/core';
import { HttpClient, HttpEvent, HttpEventType } from '@angular/common/http';
import { Observable, map, timeout } from 'rxjs';
import { API_CONFIG } from '../constants/api.constants';

export interface BackupInfo {
  id: string;
  user_id: string;
  description: string;
  timestamp: string;
  filename: string;
  file_size: number;
  file_size_mb: number;
  database_name: string;
  uploaded?: boolean;
}

export interface BackupListResponse {
  backups: BackupInfo[];
}

export interface BackupCreateResponse {
  success: boolean;
  backup_id: string;
  filename: string;
  file_size_mb: number;
  timestamp: string;
  message: string;
}

export interface BackupRestoreResponse {
  success: boolean;
  message: string;
  restored_at: string;
  backup_timestamp: string;
}

export interface LatestBackupResponse {
  success: boolean;
  latest_backup: {
    timestamp: string;
    description: string;
    file_size_mb: number;
  } | null;
}

export interface RestoreProgressEvent {
  phase: string;
  progress: number;
  message: string;
  error?: boolean;
  success?: boolean;
  data?: {
    restored_at: string;
    backup_timestamp: string;
  };
}

@Injectable({
  providedIn: 'root'
})
export class BackupService {
  private readonly API_URL = `${API_CONFIG.BASE_URL}/backup`;

  constructor(private http: HttpClient, private ngZone: NgZone) {}

  /**
   * Új adatbázis backup létrehozása
   */
  createBackup(description: string = 'Manuális biztonsági mentés'): Observable<BackupCreateResponse> {
    const formData = new FormData();
    formData.append('description', description);
    
    return this.http.post<BackupCreateResponse>(`${this.API_URL}/create`, formData);
  }

  /**
   * Backup visszaállítása
   */
  restoreBackup(backupId: string): Observable<BackupRestoreResponse> {
    return this.http.post<BackupRestoreResponse>(`${this.API_URL}/restore/${backupId}`, {})
      .pipe(timeout(600000));
  }

  /**
   * Backup visszaállítása SSE progress streammel
   */
  restoreBackupStream(backupId: string): Observable<RestoreProgressEvent> {
    return new Observable<RestoreProgressEvent>(observer => {
      const token = localStorage.getItem('token');
      const url = `${this.API_URL}/restore-stream/${backupId}?token=${encodeURIComponent(token || '')}`;

      console.log('[SSE] Connecting to:', url);
      const eventSource = new EventSource(url);

      eventSource.onopen = () => {
        console.log('[SSE] Connection opened, readyState:', eventSource.readyState);
      };

      eventSource.onmessage = (event) => {
        console.log('[SSE] Message received:', event.data);
        this.ngZone.run(() => {
          try {
            const data: RestoreProgressEvent = JSON.parse(event.data);
            observer.next(data);

            if (data.success || data.error) {
              eventSource.close();
              if (data.error) {
                observer.error(new Error(data.message));
              } else {
                observer.complete();
              }
            }
          } catch (e) {
            console.error('SSE parse hiba:', e);
          }
        });
      };

      eventSource.onerror = (err) => {
        console.error('[SSE] Error, readyState:', eventSource.readyState, err);
        this.ngZone.run(() => {
          eventSource.close();
          observer.error(new Error('Kapcsolat megszakadt a szerverrel'));
        });
      };

      return () => {
        eventSource.close();
      };
    });
  }

  /**
   * Biztonsági mentések listázása
   */
  getBackupList(): Observable<BackupInfo[]> {
    return this.http.get<BackupListResponse>(`${this.API_URL}/list`)
      .pipe(map(response => response.backups));
  }

  /**
   * Backup törlése
   */
  deleteBackup(backupId: string): Observable<{ success: boolean; message: string }> {
    return this.http.delete<{ success: boolean; message: string }>(`${this.API_URL}/${backupId}`);
  }

  /**
   * Backup fájl letöltése
   */
  downloadBackup(backupId: string): Observable<Blob> {
    return this.http.get(`${this.API_URL}/download/${backupId}`, {
      responseType: 'blob'
    });
  }

  /**
   * Backup fájl feltöltése
   */
  uploadBackup(file: File, description: string): Observable<any> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('description', description);
    
    return this.http.post(`${this.API_URL}/upload`, formData, {
      reportProgress: true,
      observe: 'events'
    }).pipe(
      map((event: HttpEvent<any>) => {
        switch (event.type) {
          case HttpEventType.UploadProgress:
            if (event.total) {
              const progress = Math.round(100 * event.loaded / event.total);
              return { status: 'progress', progress };
            }
            break;
          case HttpEventType.Response:
            return { status: 'complete', body: event.body };
        }
        return { status: 'uploading' };
      })
    );
  }

  /**
   * Legutóbbi backup információ lekérése
   */
  getLatestBackup(): Observable<LatestBackupResponse> {
    return this.http.get<LatestBackupResponse>(`${this.API_URL}/latest`);
  }

  /**
   * Legutóbbi MANUÁLIS backup információ lekérése
   */
  getLatestManualBackup(): Observable<LatestBackupResponse> {
    return this.http.get<LatestBackupResponse>(`${this.API_URL}/latest/manual`);
  }

  /**
   * Backup fájl név formázása (magyar dátum formátum)
   */
  formatBackupDate(timestamp: string): string {
    const date = new Date(timestamp);
    return date.toLocaleDateString('hu-HU', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  /**
   * Fájlméret formázása
   */
  formatFileSize(sizeInBytes: number): string {
    if (sizeInBytes < 1024) return `${sizeInBytes} B`;
    if (sizeInBytes < 1024 * 1024) return `${(sizeInBytes / 1024).toFixed(1)} KB`;
    if (sizeInBytes < 1024 * 1024 * 1024) return `${(sizeInBytes / (1024 * 1024)).toFixed(1)} MB`;
    return `${(sizeInBytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
  }
}
